import DocumentLockIcon from '@/assets/document-lock.svg?react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import {
  DatabaseIcon,
  getDatabaseSourceDisplayName,
} from '@/pages/TableViews/utils/databaseSources';
import type { TableViewColumnDto, TableViewRowDto } from '@ditto/shared';
import { ExternalLink, Eye, X } from 'lucide-react';
import { useEffect, useState, type FC } from 'react';
import { useParams } from 'react-router-dom';
import { NormalCellDetails, OptionCellDetails } from './CellDetails';
import { TableViewDocumentPreview } from './TableViewDocumentPreview';

export type SourceSidePanelProps = {
  row: TableViewRowDto | null;
  columns: TableViewColumnDto[];
  onClose: () => void;
};

export const SourceSidePanel: FC<SourceSidePanelProps> = ({
  row,
  columns,
  onClose,
}) => {
  const { tableView_id: tableViewId } = useParams<{ tableView_id: string }>();
  const [isPreviewOpen, setIsPreviewOpen] = useState(true);

  useEffect(() => {
    if (row?.fileId) {
      setIsPreviewOpen(true);
    }
  }, [row?.id, row?.fileId]);

  const getRowDisplayName = () => {
    if (row?.label) return row.label;
    if (row?.fileName) return row.fileName;
    return 'Row details';
  };

  const hasRowFile = Boolean(row?.fileId);
  const rowSourceName = getDatabaseSourceDisplayName(row?.source);

  const getGoToPreviewButtonText = () => {
    if (hasRowFile) return 'View document';
    if (row?.source === 'access_gudid')
      return 'View database item on AccessGUDID';
    if (row?.source === 'pubmed') {
      return 'View database item on PubMed';
    }
    if (row?.source === 'openalex') {
      return 'View database item on OpenAlex';
    }
    if (row?.source === 'clinical_trials') {
      return 'View clinical trial on ClinicalTrials.gov';
    }
    if (row?.source === 'fda_510k_submissions') {
      return 'View 510(k) summary on FDA website';
    }
    if (row?.source === 'premarket_approval') {
      return 'View PMA details on FDA website';
    }
    return 'View item';
  };

  const getRowBadge = () => {
    if (row?.fileName) {
      return (
        <Badge variant="outline" className="w-fit rounded-lg">
          <FlexRow alignItems="center" gap="2">
            <DocumentLockIcon className="w-4 h-4" />
            <Text className="text-sm font-medium">Document</Text>
          </FlexRow>
        </Badge>
      );
    }
    if (row?.source) {
      return (
        <Badge variant="outline" className="w-fit rounded-lg">
          <FlexRow alignItems="center" gap="2">
            <DatabaseIcon sourceId={row.source} className="h-4" />
            <Text className="text-sm font-medium">{rowSourceName}</Text>
          </FlexRow>
        </Badge>
      );
    }
    return null;
  };

  const onPreviewClick = () => {
    if (!row) return;

    if (hasRowFile) {
      setIsPreviewOpen(true);
      return;
    }

    // Find the Preview Link column and use its value
    const previewLinkColumn = columns.find(col => col.name === 'Preview Link');

    if (previewLinkColumn) {
      const previewCell = row.cells.find(
        c => c.columnId === previewLinkColumn.id
      );
      const previewLink = previewCell?.textValue;

      if (previewLink) {
        window.open(previewLink, '_blank');
        return;
      }
    }

    // Fallback: No preview link found
    console.warn('No preview link found for this row');
  };

  if (!row) return null;

  return (
    <>
      {isPreviewOpen && row.fileId && (
        <TableViewDocumentPreview
          fileId={row.fileId}
          fileName={row.fileName || 'Document'}
          onClose={() => setIsPreviewOpen(false)}
        />
      )}

      <FlexCol className="w-[360px] h-full bg-white border border-neutral-200 rounded-2xl shadow-lg flex-shrink-0 overflow-hidden">
        {/* Header */}
        <div className="border-b border-neutral-200 p-6 shrink-0">
          <FlexRow gap="4" justifyContent="between" alignItems="start">
            <FlexCol gap="3">
              <Text className="text-[11px] font-medium text-neutral-600 uppercase tracking-[1px]">
                Row Overview
              </Text>

              <Ellipsis asChild lineClamp={3} hasTooltip>
                <Text className="font-semibold text-2xl leading-tight tracking-tight">
                  {getRowDisplayName()}
                </Text>
              </Ellipsis>

              {getRowBadge()}
            </FlexCol>
            <Button
              variant="outline"
              onClick={onClose}
              className="h-10 w-10 shrink-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </FlexRow>
        </div>

        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto border-b border-neutral-200">
          <FlexCol gap="8" className="px-6 py-6">
            {columns.map((column, index) => {
              const cell = row.cells.find(c => c.columnId === column.id);
              if (!cell) return null;

              const CellComponent =
                column.type === 'options'
                  ? OptionCellDetails
                  : NormalCellDetails;

              return (
                <FlexCol key={column.id} gap="8">
                  <CellComponent
                    cell={cell}
                    column={column}
                    columns={columns}
                    row={row}
                    tableViewId={tableViewId ?? ''}
                  />
                  {index < columns.length - 1 && (
                    <div className="py-0.5">
                      <div className="h-px w-full bg-neutral-200" />
                    </div>
                  )}
                </FlexCol>
              );
            })}

            {columns.length === 0 && (
              <Text variant="body-sm" color="tertiary">
                No columns defined yet. Add columns to see extraction results.
              </Text>
            )}
          </FlexCol>
        </div>

        {/* Footer CTA */}
        <div className="p-4 shrink-0">
          <Button
            onClick={onPreviewClick}
            className="w-full bg-yellow-400 hover:bg-yellow-500 text-black"
          >
            {hasRowFile ? (
              <>
                <Eye className="w-3.5 h-3.5 mr-2" />
                {getGoToPreviewButtonText()}
              </>
            ) : (
              <>
                {getGoToPreviewButtonText()}
                <ExternalLink className="w-3.5 h-3.5 ml-2" />
              </>
            )}
          </Button>
        </div>
      </FlexCol>
    </>
  );
};
